/**
 * Chrome's Local Network Access (LNA): a page from a public origin — the cloud portal — may reach a private address —
 * a studio server's `https://n-<id>.<tenant>.<node domain>`, which resolves to its LAN IP — only once the user allows
 * it. Until then Chrome refuses the request ("Permission was denied for this request to access the `local` address
 * space"), which a page sees as the same `TypeError` a name that does not resolve gives. The node's CORS is not the
 * problem (its `/health` answers the preflight: `server.ts`).
 *
 * What this module decides, from the permission as `navigator.permissions` reports it:
 *
 *   granted       probe as usual
 *   prompt        hold the background probes of local servers: a probe fired at page load would pop a permission
 *                 prompt nobody asked for; the operator's "Connect to local servers" (a click: Chrome shows its
 *                 prompt then) probes them, and a change of the permission probes them again at once
 *   denied        hold them too — every probe is one more refusal (and a console error): the hint says how to allow
 *                 it (site settings → Local network access), and a change of the permission resumes probing
 *   unsupported   a browser without the permission (Firefox, Safari, an older Chrome): probe as usual
 *   not-needed    the page itself is on the LAN (served by a node): nothing to ask
 *
 * The permission's name is feature-detected: `local-network-access` (Chrome 142+), then `local-network` (the name
 * later Chrome versions split it into, with `loopback-network` for 127.0.0.0/8, which ACP never needs); a browser
 * that knows neither throws, and reads as `unsupported`.
 */

export type LocalNetworkAccess = 'granted' | 'prompt' | 'denied' | 'unsupported' | 'not-needed';

/** The permission names tried, in order. */
export const LOCAL_NETWORK_PERMISSION_NAMES: readonly string[] = ['local-network-access', 'local-network'];

/** What this module needs of a `PermissionStatus`. */
export type PermissionStatusPort = {
  readonly state: string;
  addEventListener(type: 'change', listener: () => void): void;
  removeEventListener(type: 'change', listener: () => void): void;
};

/** What this module needs of `navigator.permissions` (the browser's is such a port: `app/browserPorts.ts`). */
export type PermissionsPort = {
  query(descriptor: { name: string }): Promise<PermissionStatusPort>;
};

/** The connectivity manager's view of the permission: read once, then followed. */
export type LocalNetworkPort = {
  readonly read: () => Promise<LocalNetworkAccess>;
  /** Called with the new value whenever the permission changes (the user allowed or blocked it); returns an unsubscribe. */
  readonly watch: (listener: (access: LocalNetworkAccess) => void) => () => void;
};

/** A permission state as this module names it; anything else (a future state) reads as `unsupported`: probe as usual. */
export function accessOf(state: string): LocalNetworkAccess {
  return state === 'granted' || state === 'prompt' || state === 'denied' ? state : 'unsupported';
}

/** Whether background probes of a local server are held (the module comment). */
export function holdsLocalProbes(access: LocalNetworkAccess): boolean {
  return access === 'prompt' || access === 'denied';
}

/**
 * The port over `navigator.permissions` (null: the browser has none), for a page from `pageKind`'s origin. A node's
 * page needs nothing (`not-needed`); the first permission name the browser knows is the one read and watched.
 */
export function localNetworkPort(permissions: PermissionsPort | null, pageKind: 'cloud' | 'node'): LocalNetworkPort {
  let status: Promise<PermissionStatusPort | null> | null = null;
  const statusOnce = (): Promise<PermissionStatusPort | null> => {
    status ??= firstKnown(permissions);
    return status;
  };
  if (pageKind === 'node') return { read: async () => 'not-needed', watch: () => () => undefined };
  return {
    read: async () => {
      const found = await statusOnce();
      return found === null ? 'unsupported' : accessOf(found.state);
    },
    watch: (listener) => {
      let stop: (() => void) | null = null;
      let stopped = false;
      void statusOnce().then((found) => {
        if (found === null || stopped) return;
        const onChange = (): void => listener(accessOf(found.state));
        found.addEventListener('change', onChange);
        stop = () => found.removeEventListener('change', onChange);
      });
      return () => {
        stopped = true;
        stop?.();
      };
    },
  };
}

async function firstKnown(permissions: PermissionsPort | null): Promise<PermissionStatusPort | null> {
  if (permissions === null) return null;
  for (const name of LOCAL_NETWORK_PERMISSION_NAMES) {
    try {
      return await permissions.query({ name });
    } catch {
      // A name this browser does not know: `TypeError` — try the next.
    }
  }
  return null;
}
